import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import type { Game, BggSearchResult } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";

const SEARCH_GAMES_QUERY = gql`
  query SearchGames($query: String!) {
    searchGames(query: $query) {
      id
      title
      yearPublished
      thumbnailUrl
      languageIndependent
      suggestedPrice
      suggestedPriceInfo {
        price
        newPrice
        shopName
      }
      shopListings {
        id
        shopName
        listingUrl
        price
        listingName
      }
    }
  }
`;

const SEARCH_BGG_MUTATION = gql`
  mutation SearchBggGame($query: String!) {
    searchBggGame(query: $query) {
      results {
        bggId
        title
        yearPublished
      }
    }
  }
`;

const IMPORT_BGG_MUTATION = gql`
  mutation ImportBggGame($bggId: Int!) {
    importBggGame(bggId: $bggId) {
      game {
        id
        title
        yearPublished
        thumbnailUrl
        languageIndependent
        suggestedPrice
        suggestedPriceInfo {
          price
          newPrice
          shopName
        }
        shopListings {
          id
          shopName
          listingUrl
          price
          listingName
        }
      }
      errors
    }
  }
`;

interface ShopListingInfo {
  id: string;
  shopName: string;
  listingUrl: string;
  price: number | null;
  listingName: string | null;
}

interface SuggestedPriceInfo {
  price: number;
  newPrice: number;
  shopName: string;
}

export type SelectableGame = Pick<
  Game,
  "id" | "title" | "yearPublished" | "thumbnailUrl" | "languageIndependent"
> & {
  suggestedPrice?: number | null;
  suggestedPriceInfo?: SuggestedPriceInfo | null;
  shopListings?: ShopListingInfo[];
};

interface GameSelectorProps {
  initialSearch?: string;
  onSelectGame: (game: SelectableGame) => void;
  onCustomTitle: (title: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  size?: "sm" | "md";
}

export function GameSelector({
  initialSearch = "",
  onSelectGame,
  onCustomTitle,
  onCancel,
  autoFocus = true,
  size = "md",
}: GameSelectorProps) {
  const [search, setSearch] = useState(initialSearch);
  const [showDropdown, setShowDropdown] = useState(true);
  const [debouncedBggSearch, setDebouncedBggSearch] = useState("");
  const [importingBggId, setImportingBggId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (search.length < 2) {
      setDebouncedBggSearch("");
      return;
    }
    const timer = setTimeout(() => setDebouncedBggSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ["searchGames", search],
    queryFn: () =>
      graphqlClient.request<{ searchGames: SelectableGame[] }>(SEARCH_GAMES_QUERY, {
        query: search,
      }),
    enabled: search.length >= 2,
  });

  const { data: bggResults, isFetching: isBggSearching } = useQuery({
    queryKey: ["searchBggGames", debouncedBggSearch],
    queryFn: () =>
      graphqlClient.request<{ searchBggGame: { results: BggSearchResult[] } }>(
        SEARCH_BGG_MUTATION,
        { query: debouncedBggSearch }
      ),
    enabled: debouncedBggSearch.length >= 2,
  });

  const importBggMutation = useMutation({
    mutationFn: (bggId: number) =>
      graphqlClient.request<{
        importBggGame: { game: SelectableGame | null; errors: string[] };
      }>(IMPORT_BGG_MUTATION, { bggId }),
  });

  const handleImportBgg = async (bggId: number) => {
    setImportingBggId(bggId);
    try {
      const data = await importBggMutation.mutateAsync(bggId);
      if (data.importBggGame.game) {
        onSelectGame(data.importBggGame.game);
      }
    } finally {
      setImportingBggId(null);
    }
  };

  const inputClass = size === "sm" ? "input text-sm" : "input";

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus={autoFocus}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Ieškoti žaidimo pagal pavadinimą..."
          className={`${inputClass} flex-1`}
        />
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Atšaukti
          </button>
        )}
      </div>
      {showDropdown && search.length >= 2 && (
        <div className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {isSearching && !searchResults ? (
            <div className="flex items-center justify-center p-4">
              <LoadingSpinner className="h-5 w-5" />
            </div>
          ) : (
            <>
              {searchResults?.searchGames.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  onClick={() => {
                    onSelectGame(game);
                    setSearch("");
                    setShowDropdown(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50"
                >
                  {game.thumbnailUrl ? (
                    <img
                      src={game.thumbnailUrl}
                      alt={game.title}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded">
                      <GamePlaceholder />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{game.title}</p>
                    {game.yearPublished && (
                      <p className="text-xs text-gray-500">{game.yearPublished}</p>
                    )}
                  </div>
                </button>
              ))}

              {isBggSearching && (
                <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3 text-xs text-gray-400">
                  <LoadingSpinner className="h-3 w-3" />
                  <span>Ieškoma BoardGameGeek...</span>
                </div>
              )}
              {bggResults?.searchBggGame.results && bggResults.searchBggGame.results.length > 0 && (
                <>
                  <div className="border-t border-gray-100 px-4 py-1.5 text-xs font-medium text-gray-400">
                    BoardGameGeek
                  </div>
                  {bggResults.searchBggGame.results.map((result) => (
                    <button
                      key={`bgg-${result.bggId}`}
                      type="button"
                      onClick={() => handleImportBgg(result.bggId)}
                      disabled={importingBggId === result.bggId}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded bg-orange-50 text-xs font-medium text-orange-600">
                        BGG
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{result.title}</p>
                        {result.yearPublished && (
                          <p className="text-xs text-gray-500">{result.yearPublished}</p>
                        )}
                      </div>
                      {importingBggId === result.bggId && <LoadingSpinner className="h-4 w-4" />}
                    </button>
                  ))}
                </>
              )}

              {!isSearching &&
                !isBggSearching &&
                !searchResults?.searchGames.length &&
                !bggResults?.searchBggGame.results?.length && (
                  <p className="p-4 text-sm text-gray-500">Žaidimų nerasta</p>
                )}

              <div className="border-t border-gray-100 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    const title = search.trim();
                    if (!title) return;
                    onCustomTitle(title);
                    setShowDropdown(false);
                  }}
                  disabled={!search.trim()}
                  className="text-sm text-primary-700 hover:text-primary-800 disabled:opacity-50"
                >
                  Tęsti su pavadinimu „{search.trim()}"
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
